/**
 * Labels — the kit's string seam (Plan 02 D5 + the i18n convention).
 *
 * The kit is compiled into whichever application consumes it, and each app
 * owns its own i18n stack — so the kit never imports one. Every user-facing
 * string has an English default here and can be overridden per surface
 * through `BillingLabelsProvider`: a hub wraps the kit once and passes its
 * `t()`-derived labels down; the defaults keep the kit renderable standalone.
 */
import { createContext, useContext, type ReactNode } from "react";

export interface BillingLabels {
  /* Shared */
  retry: string;
  close: string;
  loading: string;
  loadMore: string;
  /* PlanBadge */
  planFree: string;
  planTier: (tier: string) => string;
  /* UsageMeter */
  meterOf: (used: string, limit: string) => string;
  meterUnlimited: (used: string) => string;
  resetsOn: (date: string) => string;
  meterDegraded: string;
  meterOverLimit: string;
  meterFileCap: string;
  meterTtsMinutes: string;
  /* UsageSheet */
  usageTitle: string;
  usageSubtitle: string;
  serviceStorage: string;
  serviceBandwidth: string;
  serviceAi: string;
  serviceTts: string;
  topUpsTitle: string;
  topUpBuy: (price: string) => string;
  paywallTitle: (service: string) => string;
  paywallBody: (service: string) => string;
  upgradeCta: string;
  /* UpgradeSheet / checkout */
  checkoutTitle: (plan: string) => string;
  couponPlaceholder: string;
  couponApply: string;
  couponValid: (amount: string) => string;
  couponInvalid: string;
  payButton: (price: string) => string;
  checkoutPending: string;
  checkoutSuccess: string;
  checkoutFailed: (detail: string) => string;
  checkoutClose: string;
  /* PaywallCard */
  paywallBlockedTitle: string;
  /* Banners */
  trialActive: (date: string) => string;
  pausedResumes: (date: string) => string;
  graceTitle: string;
  graceBody: (date: string) => string;
  pastDueTitle: string;
  pastDueBody: string;
  updatePaymentMethod: string;
  /* Settings section */
  billingSectionTitle: string;
  billingSectionEmpty: string;
  paymentMethodsTitle: string;
  paymentMethodAdd: string;
  paymentMethodDefault: string;
  paymentMethodRemove: string;
  paymentMethodRemoveConfirm: string;
  invoicesTitle: string;
  invoicesEmpty: string;
  invoiceDownload: string;
  creditNotesTitle: string;
  recoveryTitle: string;
  managePlan: string;
  /* Development-only mock provider controls (rendered only for `mock://`
   * checkout/card-session URLs, which production can never mint) */
  devSimulateTitle: string;
  devSimulateSuccess: string;
  devSimulateFailure: string;
  devCardTitle: string;
  devCardInsert: string;
}

export const EN_DEFAULTS: BillingLabels = {
  retry: "Retry",
  close: "Close",
  loading: "Loading…",
  loadMore: "Load more",
  planFree: "Free",
  planTier: (tier) => tier.charAt(0).toUpperCase() + tier.slice(1),
  meterOf: (used, limit) => `${used} of ${limit} used`,
  meterUnlimited: (used) => `${used} used`,
  resetsOn: (date) => `Resets ${date}`,
  meterDegraded: "Usage could not be read just now.",
  meterOverLimit: "Limit reached",
  meterFileCap: "File size cap",
  meterTtsMinutes: "Spoken minutes",
  usageTitle: "Usage",
  usageSubtitle: "What your account has used this period and what stays free.",
  serviceStorage: "Storage",
  serviceBandwidth: "Bandwidth",
  serviceAi: "AI",
  serviceTts: "Text-to-speech",
  topUpsTitle: "Top-ups",
  topUpBuy: (price) => `Buy for ${price}`,
  paywallTitle: (service) => `${service} limit reached`,
  paywallBody: (service) => `You have used up the free ${service} allowance for this period. Top up to keep going.`,
  upgradeCta: "Top up",
  checkoutTitle: (plan) => `Checkout — ${plan}`,
  couponPlaceholder: "Coupon code",
  couponApply: "Apply",
  couponValid: (amount) => `Coupon applied — ${amount} off`,
  couponInvalid: "That coupon is not valid for this plan.",
  payButton: (price) => `Pay ${price}`,
  checkoutPending: "Waiting for the payment to complete…",
  checkoutSuccess: "Payment confirmed. Thank you.",
  checkoutFailed: (detail) => `The payment did not go through. ${detail}`,
  checkoutClose: "Close",
  paywallBlockedTitle: "This action is unavailable right now",
  trialActive: (date) => `Your trial runs until ${date}.`,
  pausedResumes: (date) => `Your subscription is paused and resumes ${date}.`,
  graceTitle: "Payment issue — limited access",
  graceBody: (date) => `A renewal could not be charged. Access stays limited until ${date} while this is resolved.`,
  pastDueTitle: "Payment retry in progress",
  pastDueBody: "Your subscription's renewal failed and is being retried. Nothing changes for you yet.",
  updatePaymentMethod: "Update payment method",
  billingSectionTitle: "Billing",
  billingSectionEmpty: "Billing surfaces appear once a metered service is in use.",
  paymentMethodsTitle: "Payment methods",
  paymentMethodAdd: "Add payment method",
  paymentMethodDefault: "Default",
  paymentMethodRemove: "Remove",
  paymentMethodRemoveConfirm: "Remove this payment method?",
  invoicesTitle: "Invoices",
  invoicesEmpty: "No invoices yet.",
  invoiceDownload: "Download",
  creditNotesTitle: "Credit notes",
  recoveryTitle: "Action required",
  managePlan: "Manage plan",
  devSimulateTitle: "Local development — mock provider",
  devSimulateSuccess: "Simulate successful payment",
  devSimulateFailure: "Simulate failed payment",
  devCardTitle: "Local development — mock card session",
  devCardInsert: "Store test card",
};

export const BillingLabelsContext = createContext<BillingLabels>(EN_DEFAULTS);

/** Override any subset of the English defaults for a subtree — the one seam
 *  an application's i18n uses to drive the kit's strings. */
export function BillingLabelsProvider({
  labels,
  children,
}: {
  labels: Partial<BillingLabels>;
  children: ReactNode;
}) {
  return (
    <BillingLabelsContext.Provider value={{ ...EN_DEFAULTS, ...labels }}>{children}</BillingLabelsContext.Provider>
  );
}

/** Read the active labels — English defaults unless a provider overrode them. */
export function useBillingLabels(): BillingLabels {
  return useContext(BillingLabelsContext);
}
